/* prettier-ignore */

import { Component, computed, inject, ViewEncapsulation } from "@angular/core";
import { ToolsService } from "./services/tools.service";
import { Router, RouterOutlet } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { NavigationService } from "./services/navigation.service";
import { RestService } from "./services/rest.service";
import { AuthService } from "./auth/services";
import { ModalController } from "@ionic/angular";
import { UserService } from "./user/services";
import { StatisticsService } from "./services/statistics.service";
import { GoogleTranslateService } from "./services/google-translate.service";
import { fadeInAnimation } from './route-animations';
import { Location } from '@angular/common';
import { CookieService } from "ngx-cookie-service";
import { DebugService } from "./services/debug.service";
import { ChatBotScriptService } from "./services/chatbot.service";
import { NewCustomRouteService } from "./pages/new-custom-route/new-custom-route.service";
import { BreakpointsService } from "./services/breakpoints.service";
import '@iurban-malaga/cicerone-ui';
import { GamificationService } from "./shared/gamification/services/gamification.service";
import { Passport } from "./shared/gamification/interfaces/passport.interface";
import { LocalStorageService } from "./services";
import { UserUpdatedResponse } from "./user/interfaces";

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
  encapsulation: ViewEncapsulation.None,
  animations: [ fadeInAnimation ] 
})

export class AppComponent {
  private debugService = inject(DebugService);
  private customRouteService = inject(NewCustomRouteService);
  private _gamificationService = inject(GamificationService);
  public breakpointsService = inject(BreakpointsService);
  private _localStorageService = inject(LocalStorageService);

  public isRegisterMode = computed(() => this.tools.isRegisterMode())
  public isPlannerMode = computed(() => this.tools.isPlannerMode())
  public isAppLoaded = computed(() => this.tools.isAppLoaded());
  public isTechMode = computed(() => this.tools.isTechMode());
  private isInfoRoutePage = computed(() => this.tools.isInfoRoutePage());
  public showPassport = computed(() => this._gamificationService.passportPopup() || this._gamificationService.passportBottomSheet());  
  
  queryParamLang: any;
  chatId: any;
  
  private _userDataLocalStorage: UserUpdatedResponse = this._localStorageService.getItem('user') ?? null; // TODO: DELETE FOR SSR -> NEW LOGIN STRATEGY (SECURE COOKIE)
  constructor(
    public modalCtrl: ModalController,
    public translate: TranslateService,
    private router: Router,
    public tools: ToolsService,
    public rest: RestService,
    public navigation: NavigationService,
    public authService: AuthService,
    public userService: UserService,
    public statsService: StatisticsService,
    public googleTranslateService: GoogleTranslateService,
    private cookieService: CookieService,
    private chatService: ChatBotScriptService,
    private location: Location
  ) {
    // Init logged user
    if (this._userDataLocalStorage) { // TODO: DELETE FOR SSR -> NEW LOGIN STRATEGY (SECURE COOKIE)
      this.authService.setUser(this._userDataLocalStorage);
    } else {
      this.authService.setUser(null);
    }
    
      this.checkWebApp();
      
      // ! PENSAR COMO HACERLO ESCALABLE
      const currentUrl = window.location.href;
      if(currentUrl.includes('turismo.yecla.es')) {
        this.rest.subDomain = 'turismoyecla'
      } else {
        this.rest.subDomain = this.tools.getSubdomain(window.location.href);
      }
      
      this.debugService.info(this.rest.subDomain, '[AppComponent] SUBDOMAIN');
      this.checkPreProduction();
      this.navigation.startSaveHistory();
      this.translate.setDefaultLang("es");
      this.translate.use("es");
      this.tools.setCorporiveColors();
  }

  ngOnInit(): void {    
    if (window.location?.pathname?.includes('survey-id'))
      return;

    const queryParams = window.location.search ? window.location.search : "";
    const params = new URLSearchParams(queryParams);
    const loadAppFromParam = this.getQueryParam(params);
    this.initializeParams(loadAppFromParam);
    this.checkTechMode();
    this.checkFromDistributor();
    this.checkToken();

    if (this.rest.subDomain) {
      this.rest.getCommonApp(this.statsService.token).subscribe((response) => {
        this.debugService.debug(response, '[AppComponent] Common API Response');

        if(this.isRegisterMode()){
          this.setRegisterMode(response);
          return;
        }

        if(this.isPlannerMode()){
          this.setPlannerMode(response);
          return;
        }

        if (this.router.url.includes("survey-id")) 
          return;

        if (response.error) {
          this.router.navigateByUrl("not-found");
          return;
        }

        this.enablePassportAndSetDataIfExist(response.passport);
        this.checkPolylinesActive(response?.location?.visualizeLines);

        this.rest.setDevice(response);
        this.debugService.debug(this.rest.device, '[AppComponent] Device Info');
        this.setLang();

        if(this.checkInfoRoute(response))
          return;

        if (response?.type === 'app'){
          this.userService.checkAllUserEntries();
          this.tools.setIsApp(true);
          this.tools.setAppStarted()
        }
          
        if (response?.type === 'distributor'){
          this.tools.setIsDistributor(true);
          this.tools.setAppStarted()
        }
  
        if (!this.router.url.includes('tech-edit')){
          this.enableChat(response)
        }

      })
      return;
    } 
    this.router.navigateByUrl("not-found");
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet && outlet.activatedRouteData && outlet.activatedRouteData['animation'];
  }

  increaseClick() {
    if (!this.tools.showHeader()) 
      return;

    this.statsService.sumClicks();
    this.statsService.setLocalStorageSessionIDFromClick();
    this.statsService.setLocalStorageTokenFromClick();
  }

  getQueryParam(queryParam: URLSearchParams) {
    let params = {};

    if (queryParam.get("lang")) 
      params["lang"] = queryParam.get("lang");

    if (queryParam.get("distributor"))  
      params["distributor"] = queryParam.get("distributor");
    
    if (queryParam.get("token")) 
      params["token"] = queryParam.get("token");
    
    if (queryParam.get("id")) 
      params["id"] = queryParam.get("id");
    
    if (queryParam.get("techMode")) 
      params["techMode"] = queryParam.get("techMode");
    
    if (queryParam.get("showDebug")) 
      params["showDebug"] = queryParam.get("showDebug");

    if (queryParam.get("passport")) 
      params["passport"] = queryParam.get("passport");
    
    if (queryParam.get("enableMic")) 
      params["enableMic"] = queryParam.get("enableMic");
    
    if (queryParam.get("planner")) 
      params["planner"] = queryParam.get("planner");
    
    return Object.keys(params).length > 0 ? params : "";
  }

  initializeParams(params) {
    this.debugService.info(params, '[AppComponent] Params');
    this.tools.homeDistributor = null;
    
    if (params["enableMic"]) 
      this.tools.chatType.set('audio');

    if (params["showDebug"]) {
      this.debugService.enableLogs();
      this.location.replaceState('');
    }

    if (params['techMode']){
      this.debugService.info('Enabling Tech Mode', '[AppComponent]');
      this.tools.isTechMode.set(true);
      this.tools.setWithExpiry('techMode', true);
      this.location.replaceState('');
    }

    if (params["distributor"]) {
      this.tools.homeDistributor =  this.tools.getDistributorUrl(params["distributor"]);
      this.tools.setWithExpiry('homeDistributor', this.tools.homeDistributor);
      this.location.replaceState('');
    };

    if (params["token"]) {
      this.statsService.token = params["token"];
      this.rest.token = params["token"];
      this.debugService.warn('Setting new token', '[AppComponent]');
      this.tools.setWithExpiry('token', params['token']);
      this.location.replaceState('');
    }

    if (params["lang"]) {
      this.queryParamLang = params["lang"];
      this.location.replaceState('');
    }

    if (params["planner"]) {
      this.debugService.info('Enabling Planner Mode', '[AppComponent]');
      this.tools.isPlannerMode.set(true);
      // Don't persist to localStorage - planner mode is iframe-specific and should only work with query param
      this.location.replaceState('');
    }

    this.statsService.setSessionId();
    if (!params["token"]) 
      this.statsService.setToken();
  }

  checkPreProduction(){
    const preProduction =  this.cookieService.get('preProduction');
    if (!preProduction) 
      return;

    this.rest.subDomain = preProduction.trim();
    this.rest.apiUrlBase = 'https://pre.ciceroneweb.com';
    this.tools.isPreProduction.set(true);
  }

  checkTechMode(){
    const isTechMode = this.tools.getWithExpiry('techMode');
    if (!isTechMode)
      return;

    this.authService.logout();
    this.userService.resetAll();
    this.tools.isTechMode.set(true);
  }

  checkFromDistributor(){
    const fromDistributor = this.tools.getWithExpiry('homeDistributor');
    if (fromDistributor)
      this.tools.homeDistributor = fromDistributor
  }

  checkToken(){
    this.debugService.info('Checking token', '[AppComponent]');
    if (this.statsService.token)
      return;

    const token = this.tools.getWithExpiry("token");
    if (token) {
      this.debugService.info('Restoring Found Token', '[AppComponent]');
      this.statsService.token = token;
      this.rest.token = token;
      this.tools.setWithExpiry("token", token);
    } 
    else 
      this.debugService.warn('Token NOT Found', '[AppComponent]');
  }

  checkPolylinesActive(visualizeLines){
    this.customRouteService.showPolylines.set(visualizeLines ?? true)
  }

  checkInfoRoute(response){
    if (this.isInfoRoutePage()){
      if (response?.type !== 'app' || response?.type !==  'distributor')
        return false;

      if (response?.type === 'app')
        this.tools.setIsApp(true);

      if (response?.type === 'distributor')
        this.tools.setIsDistributor(true);

      this.tools.setCorporativeColors();
      this.tools.enableHeader();
      return true;
    }
  }

  // setLang(){
  //   this.tools.headerLangType.set(this.rest?.device?.headerLangType ?? 'flags')
  //   this.tools.setLanguages(this.rest?.device?.languages);
  //   const mainLang = this.rest?.device?.mainLanguage || "es";
  //   const cookieLang = this.googleTranslateService.getCookieLang();
  //   if (cookieLang){
  //     this.tools.changeLanguage(mainLang)
  //     this.googleTranslateService.currentLang = cookieLang;
  //   }
  //   else 
  //     this.googleTranslateService.changeLanguage(this.queryParamLang || this.googleTranslateService.getLocalLang() || mainLang);
  // }

  setLang(){
    this.tools.headerLangType.set(this.rest?.device?.headerLangType ?? 'flags')
    this.tools.setLanguages(this.rest?.device?.languages);
    const browserLang = this.googleTranslateService.getBrowserLang();
    const mainLang = this.rest?.device?.mainLanguage || browserLang || "es";
    this.googleTranslateService.changeLangOnInit(
      this.queryParamLang || this.googleTranslateService.getLocalLang() || mainLang,
      this.rest?.device?.languages
    )
  }

  setRegisterMode(response){
    if (response.error)
      return;

    this.rest.setDevice(response);
    this.tools.setCorporativeColors();
    const mainLang = this.rest?.device?.mainLanguage || "es";
    this.tools.setLanguages(this.rest?.device?.languages);
    this.googleTranslateService.changeLanguage(mainLang)
    this.tools.enableHeader();
    this.enableChat(response)
  }

  setPlannerMode(response){
    if (response.error) {
      this.router.navigateByUrl("not-found");
      return;
    }

    this.rest.setDevice(response);
    this.tools.setCorporativeColors();
    // const mainLang = this.rest?.device?.mainLanguage || "es";
    // const apiLangs = this.rest?.device?.languages || [];
    // this.tools.setLanguages(apiLangs);

    this.tools.disableHeader();
    this.customRouteService.initializeData();
    this.setLang();

    // Auto-detect user language
    // this.setAutoDetectedLanguage(mainLang, apiLangs);
    
    // Set app type based on response but keep header disabled
    if (response?.type === 'app'){
      this.tools.setIsApp(true);
    }
      
    if (response?.type === 'distributor'){
      this.tools.setIsDistributor(true);
    }
    
    // Redirect to register-user if not already there
    if (!this.router.url.includes('register-user') && !this.router.url.includes('custom-route')) {
      this.router.navigateByUrl('/register-user');
    }
  }

  // setAutoDetectedLanguage(mainLang: string, apiLangs: string[]){
  //   // Priority: 1. Existing cookie lang, 2. Browser lang (if in API langs), 3. Browser lang (Google Translate), 4. Main lang
  //   const cookieLang = this.googleTranslateService.getCookieLang();
  //   if (cookieLang) {
  //     this.debugService.info(`Using existing cookie language: ${cookieLang}`, '[AppComponent]');
  //     this.googleTranslateService.currentLang = cookieLang;
  //     return;
  //   }

  //   const browserLang = this.googleTranslateService.getBrowserLang();
  //   this.debugService.info(`Detected browser language: ${browserLang}`, '[AppComponent]');
    
  //   if (browserLang && apiLangs.includes(browserLang)) {
  //     // Browser language is available in API translations
  //     this.debugService.info(`Using browser language from API: ${browserLang}`, '[AppComponent]');
  //     this.googleTranslateService.changeLanguage(browserLang);
  //     return;
  //   }
    
  //   if (browserLang) {
  //     // Use Google Translate for browser language
  //     this.debugService.info(`Using browser language with Google Translate: ${browserLang}`, '[AppComponent]');
  //     this.googleTranslateService.changeLangOnInit(browserLang, apiLangs);
  //     return;
  //   }
    
  //   // Fallback to main language
  //   this.debugService.info(`Using main language fallback: ${mainLang}`, '[AppComponent]');
  //   this.googleTranslateService.changeLanguage(mainLang);
  // }

  enableChat(response){
    this.customRouteService.initializeData();
    this.rest.device.chatBotId = response?.lang?.chatbot;
    this.rest.device.chatBotStandalone = response?.newVersion === 1;
    const chatType = response?.lang?.chatbotMicEnabled === true || this.tools.chatType() === 'audio' ? 'audio' :'text';
    const mainLang = this.rest.device.mainLanguage ? this.rest.device.mainLanguage : "es";
    const newChat =  this.cookieService.get('newChat');
    if (this.rest.device.chatBotId && (this.rest.device.chatBotStandalone || newChat === 'true')){
      this.chatId = this.rest.device.chatBotId;
      this.tools.chatType.set(chatType);
      //Enable Audio Hardcoded for mariñas client
      if (this.chatId === '127'){
        this.tools.chatType.set('audio');
      }
      return;
    }
    this.chatService.loadChat(mainLang);
  }

  private checkWebApp(){
    const isWebApp = navigator?.userAgent?.includes("CiceroneWebApp");
    this.tools.isWebApp.set(isWebApp);
  }

  private enablePassportAndSetDataIfExist(passport: Passport){
    if (!passport)
      return;

    this._gamificationService.setPassportsEnabled();

    if (passport.popup)
      console.log('Setting passport popup', '[AppComponent]', passport.popup);
      this._gamificationService.setPassportPopup(passport.popup);

    if (passport.bottom_sheet)
      this._gamificationService.setPassportBottomSheet(passport.bottom_sheet);
  }
}